import { useToasts } from "react-toast-notifications";
import service from "../../../../../api/services";
import {
    HandleFileUploadProps,
    HandleSubmitProps,
    LostBarcodeProps,
} from "../interface";
import * as XLSX from "xlsx";

export const useLostBarcodeLogic = () => {
    const { addToast } = useToasts();

    // INFO:- function to modified data for table
    const transformData = (
        data: { "barcode no": number; remarks: string }[]
    ): LostBarcodeProps[] => {
        return data?.map((item) => ({
            barcode: item["barcode no"]?.toString() ?? "",
            remarks: item.remarks,
            error: !item["barcode no"] || !item?.remarks,
            apiResponse: "",
        }));
    };
    // end

    // INFO:- function to convert the xlsx document to json format
    const handleFileUpload = ({
        e,
        setIsLoading,
        setLostBarcodeData,
        setErroredBarcode,
    }: HandleFileUploadProps) => {
        // Set loading state to true while processing the file
        setIsLoading(true);

        const reader = new FileReader();

        // Event handler for successful file reading
        reader.onload = (event: ProgressEvent<FileReader>) => {
            try {
                const data = event.target?.result;

                // Ensure valid file data before processing
                if (typeof data === "string") {
                    // Read and parse Excel file using XLSX library
                    const workbook = XLSX.read(data, { type: "binary" });

                    const parsedData: { [key: string]: any[] } = {};
                    workbook.SheetNames.forEach((sheetName) => {
                        const sheet = workbook.Sheets[sheetName];
                        parsedData["lost_barcode"] = XLSX.utils.sheet_to_json(sheet);
                    });

                    // If parsed data is available, update the barcode state
                    if (parsedData) {
                        setLostBarcodeData(transformData(parsedData?.lost_barcode));
                        setErroredBarcode([]); // Clear previous errors
                    }
                } else {
                    throw new Error("Invalid file data");
                }
            } catch (error) {
                console.error("Error parsing Excel file:", error);
            } finally {
                // Ensure loading state is reset after processing
                setIsLoading(false);
            }
        };

        // Event handler for file reading errors
        reader.onerror = (event: ProgressEvent<FileReader>) => {
            console.error("Error reading the file:", event?.target?.error);
            setIsLoading(false);
        };

        // Validate file selection before reading
        if (e.target.files && e.target.files[0]) {
            reader.readAsBinaryString(e.target.files[0]);
        } else {
            console.error("No file selected");
            setIsLoading(false);
        }
    };

    // end

    // INFO:- function to handle submit button
    const handleSubmit = async ({
        excelData,
        setIsLoading,
        setErroredBarcode,
        is_recognised
    }: HandleSubmitProps) => {
        // Initialize an empty array to store error barcode data
        let modifiedData: LostBarcodeProps[] = [];

        // Process each item in `excelData` asynchronously
        await Promise.all(
            excelData?.map(async (item) => {
                if (!item.error) {
                    // If the item has no error, process it via the API
                    await handleBulkLostBarcodeApi(item, modifiedData,is_recognised);
                } else {
                    // If the item has an error, mark it accordingly and store the response
                    modifiedData?.push({
                        ...item,
                        error: true,
                        apiResponse: "Invalid Data",
                    });
                }
            })
        );

        // Set loading state to false after processing is complete
        setIsLoading(false);

        // Display a success toast message to notify the user
        addToast("File Upload successfully", {
            appearance: "success",
            autoDismiss: true,
            autoDismissTimeout: 3000,
        });

        // Update the state with modified error barcode data
        setErroredBarcode(modifiedData);
    };

    // end

    // INFO:- function to handle the api for bulk lost barcode
    const handleBulkLostBarcodeApi = async (
        payload: LostBarcodeProps,
        modifiedData: LostBarcodeProps[],
        is_recognised:boolean
    ) => {
        // Construct API payload using barcode and remarks from the input
        const apiPayload = {
            barcode: payload.barcode,
            remarks: payload.remarks,
            is_recognised:is_recognised
        };

        try {
            // Attempt to upload barcode data via the API service
            await service.barcode.bulkUploadLostBarcode(apiPayload);
        } catch (error: any) {
            // Log error message for debugging purposes
            console.error(error?.response?.data?.message);

            // Append failed item to `modifiedData` with error flag and response message
            modifiedData?.push({
                ...payload,
                error: true,
                apiResponse: error?.response?.data?.message ?? "success",
            });
        }
    };
    // end

    return {
        handleSubmit,
        handleFileUpload,
    };
};
